import { Card, Alert, Row, Col, Form, Button } from "react-bootstrap";
import { VscSend } from "react-icons/vsc";
import { GrInProgress } from "react-icons/gr";
import { VscDebugLineByLine } from "react-icons/vsc";
import { FaCheck, FaRegStopCircle } from "react-icons/fa";
import { PiWarningBold } from "react-icons/pi";
import { LuText } from "react-icons/lu";
import Tooltip from "../../../controls/common/tooltip";
import WidgetBase from "../common/widgetBase";
import Message from "./../common/message";
import { Converter } from "../../workspace/converter";
import { canMutateSession } from "../../session/mutationGate";
import Utils from "context/utils"
import Preferences, { withPreferences } from 'context/preferences';
import "./index.css";

// Exported (named) for unit tests that inspect the raw class directly; the
// default export below is the withPreferences-wrapped component used by the app.
export class ChatNode extends WidgetBase {
  constructor(props) {
    super(props);

    this.state = {
      inputMessage: "",
      execution: null,
      progress: false,
      progressMessage: null,
      error: false,
      errorMessage: null,
    };

    this.activeStatuses = new Set(["creating", "running", "stopping"]);
    this.inactiveStatuses = new Set(["completed", "failed", "stopped"]);

    this.mounted = false;
  }

  componentDidMount() {
    super.componentDidMount();
    this.mounted = true;
    this.continueAutoRefresh();
  }

  componentWillUnmount() {
    this.mounted = false;
    // The base class holds the session listener, the alias subscriptions and this widget's sync
    // hold. Not calling it leaked all three for the life of the page.
    super.componentWillUnmount();
  }

  /**
   * Sync stays on while the execution this widget started is still running.
   *
   * Pressing Send turns sync on (`sendMessage`), and that used to be the whole of it: three minutes
   * later the idle timeout stopped polling, and an agent that took longer than that answered into a
   * screen that had stopped listening. The reply was there on a refresh — which is exactly the
   * experience being fixed.
   */
  syncHold() {
    if ( this.state.progress ) {
      return "Chat — starting an execution";
    }
    const execution = this.state.execution;
    if ( execution && this.activeStatuses.has(execution["status"]) ) {
      return `Chat — execution ${execution["status"]}`;
    }
    return null;
  }

  componentDidUpdate(prevProps) {
    super.componentDidUpdate(prevProps);
    // Detect session<->execution mismatch and wipe local state if that happens
    // Detect session<->execution mismatch and wipe local state if that happens
    const session = this.context.session;
    if ( this.state.execution && session && this.state.execution["session_id"] !== session.id ) {
      this.setState({
        execution: null,
        progress: false, progressMessage: null, error: false, errorMessage: null
      });
    }
  }

  canSendMessage() {
    const si = this.props.data.shellInterface;
    return (
      !this.state.progress
      && !( this.state.execution && this.activeStatuses.has(this.state.execution["status"]) )
      // Sending a message starts a new execution (create_execution) — a session-data mutation — so it
      // requires the same gate the composer renders behind. Redundant with that render gate by design:
      // sendMessage() is also reachable from the predefined-input buttons, so it re-checks here as the
      // choke point. See session/mutationGate.js for the shared fail-open default applied when a
      // shellInterface doesn't expose the full seam.
      && canMutateSession(si)
    );
  }

  // Tooltip copy for the composer Send button. The composer and the predefined-input quick-send
  // buttons only render while mutations are possible, so there is no blocked state to explain here —
  // a disabled Send means an execution is already in flight.
  sendTooltipText() {
    return "Send a message";
  }

  sendMessage(messageText) {
    // Don't proceed if there is an active execution or there is no message
    if ( !this.canSendMessage() ) {
      return;
    }

    // Capture text before setState clears inputMessage
    const text = messageText || this.state.inputMessage;

    // Enable session synchronization
    this.props.data.shellInterface.enableSessionSync(true);

    // Clear state prior to starting an execution
    this.setState({
      inputMessage: "",
      execution: null,
      progress: true, progressMessage: "Initiating workflow execution...",
      error: false, errorMessage: null
    });

    // Construct workflow input
    const userInput = Converter.getComponentPortValueData(this.props.data.component, "user_input");
    const workflowAlias = Converter.getComponentPortValueData(this.props.data.component, "send_message_workflow");
    const inputData = [
      {
        "alias": userInput,
        "value": {
          "type": "message",
          "message": {
            "role": "user",
            "content": [
              {
                "type": "text",
                "text": text
              }
            ],
            "attributes": {
              "id": Utils.generateUniqueId(),
              "created_at": Utils.currentTimeUtcIso()
            }
          }
        }
      }
    ];

    const session = this.context.session;
    if (!session) return;
    const sessionId = session.id;
    const revisionId = this.props.data.shellInterface.getRevision()["id"];
    const body = {
      "command": "create_execution",
      "session_id": sessionId,
      "alias": workflowAlias,
      "input_data": inputData
    };
    this.props.data.shellInterface.getApp().api.revisionsIdDataCommandPost(revisionId, body)
      .then( response => {
        // Parse response
        const execution = response.data["execution"];

        // Indicate success
        this.setState({
          execution: execution,
          progress: false, progressMessage: null, error: false, errorMessage: null
        });
      }, error => {
        // Indicate failure
        this.setState({
          progress: false, progressMessage: null,
          error: true, errorMessage: "Failed to start workflow execution: " + error.message
        });
      });
  }

  continueAutoRefresh() {
    if ( !this.mounted ) {
      return;
    }
    setTimeout(() => {
      // Issue refresh if execution is still in one of active statuses
      if ( this.state.execution && this.activeStatuses.has(this.state.execution["status"]) ) {
        this.refreshExecution();
      }

      // Continue timer unconditionally
      this.continueAutoRefresh();
    }, 1000);
  }

  refreshExecution() {
    const executionId = this.state.execution["id"];
    const revisionId = this.props.data.shellInterface.getRevision()["id"];
    const body = {
      "command": "get_execution",
      "execution_id": executionId
    };
    this.props.data.shellInterface.getApp().api.revisionsIdDataQueryPost(revisionId, body)
      .then( response => {
        // Parse response
        const execution = response.data["execution"];

        // Update state
        this.setState({ execution: execution });
      }, error => {
        // Stop refreshing and hide execution details
        this.setState({ execution: null });
      });
  }

  createExecutionPath() {
    const session = this.context.session;
    const sessionId = session.id;
    const executionId = this.state.execution.id;
    return this.props.data.shellInterface.createWorkflowPath("debugger", sessionId, executionId);
  }

  stopExecution() {
    // Send command to stop execution
    const executionId = this.state.execution["id"];
    const revisionId = this.props.data.shellInterface.getRevision()["id"];
    const body = {
      "command": "stop_execution",
      "execution_id": executionId
    };
    this.props.data.shellInterface.getApp().api.revisionsIdDataCommandPost(revisionId, body)
      .then( response => {
        // Refresh execution state
        this.refreshExecution();
      }, error => {
        console.error("Failed to stop execution:", error);
      });
  }

  getClassName() {
    return "playground-chat-node";
  }

  // Where a `file` content block's bytes come from — same guard as message/index.js's fileContext():
  // degrades to undefined instead of throwing when the shellInterface or session double lacks the
  // full seam (readonly.test.js's fixture is exactly such a double).
  fileContext() {
    const session = this.context && this.context.session;
    const shellInterface = this.props.data && this.props.data.shellInterface;
    if ( !session || !shellInterface || !shellInterface.getApp || !shellInterface.getRevision ) return undefined;
    const app = shellInterface.getApp();
    const revision = shellInterface.getRevision();
    if ( !app || !app.api || !revision || !revision["id"] ) return undefined;
    return { api: app.api, revisionId: revision["id"], sessionId: session.id };
  }

  renderContent() {
    const timezone = this.props.preferences["app.timezone"];

    // Widget dimensions. In grid mode the width/height ports don't exist —
    // the cell sizes the widget instead, so fall back to 100%/100%.
    const isGrid = "grid" === this.props.data?.layoutType;
    const heightPx = Converter.getComponentPortValueData(this.props.data.component, "height");
    // Width always fills the card: the free-form `width` port sizes the CARD (WidgetBase.render), so
    // sizing the content to it independently is what left content narrower than its own widget.
    const width = "100%";
    const height = isGrid ? "100%" : `${heightPx}px`;

    // Find history data
    const [ historyValue, historyNotice ] = this.loadObjectValue(
      this.props.data.component, "history", "messages");
    let history = historyValue ? historyValue["messages"] : null;

    if ( history ) {
      // Trim to max length
      const maxMessages = Converter.getComponentPortValueData(this.props.data.component, "max_messages");
      if ( maxMessages ) {
        history = history.slice(0, maxMessages);
      }

      // Reverse for rendering purposes
      history = [...history];
      history.reverse();
    }

    // Find out what is the default content view
    const contentView = Converter.getComponentPortValueData(this.props.data.component, "content_view");
    const fileContext = this.fileContext();

    // Find user input and message workflow details
    const userInput = Converter.getComponentPortValueData(this.props.data.component, "user_input");
    const sendMessageWorkflow = Converter.getComponentPortValueData(this.props.data.component, "send_message_workflow");
    const workflowNotice
      = userInput && sendMessageWorkflow
      ? null : { variant: "info", message: "Connect \"user_input\" and \"send_message_workflow\" to enable chat." };

    // Find predefined inputs
    const predefinedInputs = Converter.getComponentPortValueData(this.props.data.component, "predefined_inputs");
    const predefinedContent = predefinedInputs && predefinedInputs["content"]
      ? predefinedInputs["content"].filter(item => item["type"] === "text" && item["text"])
      : [];

    // Retrieve session from context
    const session = this.context.session;

    // Drives whether the bottom panel renders at all (see the render block below). One rule for every
    // mutation surface in the playground: it renders only while mutations are actually possible.
    // canMutateSession() collapses ALL the reasons they might not be — no write access, a committed
    // revision, a closed session — so the send row behaves the same way whichever one applies, instead
    // of vanishing for one and lingering disabled for another. Why mutations are blocked isn't
    // surfaced anywhere — the control is simply absent.
    const canMutate = canMutateSession(this.props.data.shellInterface);

    // The single content notice to show instead of the message list: the
    // "select a session / connect a value / …" notice from the history port, or
    // a "no history" warning when the bound value is present but empty.
    const contentNotice = historyNotice
      ? historyNotice
      : (!history ? { variant: "warning", message: "The bound value has no history to display." } : null);

    // Render chat content
    return (
      <div className="playground-chat-node-content" style={{
        "width": width,
        "height": height,
        "display": "flex",
        "flexDirection": "column",
        "overflow": "hidden"
      }}>
        {/* A content notice (no session / nothing bound / no history) renders
            at the TOP of the widget — consistent with every other widget —
            inside its OWN flex:1 scrollable region so a tall notice can scroll
            instead of being clipped by the fixed-height card. The messages
            scroller (which is `column-reverse`, so a notice placed inside it
            would flip to the bottom) is only rendered when there ARE messages;
            the two are mutually exclusive, so exactly one fills the space. */}
        {contentNotice &&
          <div className="playground-chat-node-notice nodrag nowheel">
            {this.renderNotice(contentNotice)}
          </div>
        }
        {!contentNotice &&
          <Card.Body className="playground-chat-node-messages nodrag nowheel" style={{
            "flex": 1,
            "minHeight": 0,
            "overflow": "auto"
          }}>
            {history.map((messageValue, index) => (
              <Message
                key={index}
                value={messageValue}
                contentView={contentView}
                timezone={timezone}
                fileContext={fileContext}
              />
            ))}
          </Card.Body>
        }

        {this.state.progress &&
          <>
            <hr className="playground-chat-node-separator"/>
            <Card.Body className="nodrag nowheel">
              {this.renderLoading(this.state.progressMessage)}
            </Card.Body>
          </>
        }
        {this.state.error &&
          <>
            <hr className="playground-chat-node-separator"/>
            <Card.Body className="nodrag nowheel">
              <Alert variant="warning">
                {this.state.errorMessage}
              </Alert>
            </Card.Body>
          </>
        }
        {this.state.execution &&
          <>
            {this.activeStatuses.has(this.state.execution["status"]) &&
              <>
                <hr className="playground-chat-node-separator"/>
                <Card.Body className="nodrag nowheel">
                  <Card.Text>
                    <Row className="align-items-center">
                      <Col xs="auto" style={{ "padding-right": "0px" }}>
                        <GrInProgress />
                      </Col>
                      <Col>
                        <span>
                          Workflow is running... <br/>
                          <span className="text-muted" style={{ "text-wrap": "wrap" }}>
                            Created: {Utils.formatTimeAgo(this.state.execution["created_at"])}
                          </span>
                        </span>
                      </Col>
                      <Col xs="auto">
                        <Tooltip text="Open in Debugger">
                          <Button size="sm" variant="light">
                            <a href={this.createExecutionPath()}>
                              <VscDebugLineByLine />
                            </a>
                          </Button>
                        </Tooltip>
                        &nbsp;&nbsp;
                        <Tooltip text="Stop execution">
                          <Button size="sm" variant="light" disabled={"running" !== this.state.execution["status"]} onClick={(event) => {
                            this.stopExecution();
                          }}>
                            <FaRegStopCircle />
                          </Button>
                        </Tooltip>
                      </Col>
                    </Row>
                  </Card.Text>
                </Card.Body>
              </>
            }
            {this.inactiveStatuses.has(this.state.execution["status"]) &&
              <>
                <hr className="playground-chat-node-separator"/>
                <Card.Body className="nodrag nowheel">
                  <Card.Text>
                    <Row className="align-items-center">
                      <Col xs="auto" style={{ "padding-right": "0px" }}>
                        {"completed" == this.state.execution["status"] &&
                          <FaCheck />
                        }
                        {"failed" == this.state.execution["status"] &&
                          <PiWarningBold />
                        }
                        {"stopped" == this.state.execution["status"] &&
                          <FaRegStopCircle />
                        }
                      </Col>
                      <Col>
                        <span>
                          Workflow execution completed. <br/>
                          <span className="text-muted" style={{ "text-wrap": "wrap" }}>
                            Status: {this.state.execution["status"]}
                          </span>
                        </span>
                      </Col>
                      <Col xs="auto">
                        <Tooltip text="Open in Debugger">
                          <Button size="sm" variant="light">
                            <a href={this.createExecutionPath()}>
                              <VscDebugLineByLine />
                            </a>
                          </Button>
                        </Tooltip>
                      </Col>
                    </Row>
                  </Card.Text>
                </Card.Body>
              </>
            }
          </>
        }

        {/* The bottom panel — composer, predefined inputs, and the "connect the ports" notice — is
            entirely an authoring/interaction surface, so the whole thing is gated on canMutate. The
            notice in particular used to render for viewers, telling them to connect ports they have
            no way to connect. A non-mutable chat shows its history and nothing else. */}
        {(workflowNotice && canMutate) &&
          <>
            <hr className="playground-chat-node-separator"/>
            <Card.Body className="nodrag nowheel">
              {this.renderNotice(workflowNotice)}
            </Card.Body>
          </>
        }
        {(session && !workflowNotice && canMutate
          && predefinedContent.length > 0) &&
          <>
            <hr className="playground-chat-node-separator"/>
            <Card.Body className="playground-chat-node-predefined-inputs-container nodrag nowheel">
              <div className="playground-chat-node-predefined-inputs">
                {predefinedContent.map((item, index) => (
                  <Button
                    key={index}
                    size="sm"
                    variant="outline-secondary"
                    disabled={!this.canSendMessage()}
                    onClick={() => this.sendMessage(item["text"])}
                    title={item["text"]}
                  >
                    <LuText /> {item["text"]}
                  </Button>
                ))}
              </div>
            </Card.Body>
          </>
        }
        {(session && !workflowNotice && canMutate) &&
          <>
            <hr className="playground-chat-node-separator"/>
            <Card.Body className="nodrag nowheel">
              <Card.Text>
                <Row className="align-items-center">
                  <Col style={{ "padding-right": "0px" }}>
                    <Form onSubmit={(event) => {
                      event.preventDefault();
                      this.sendMessage();
                    }}>
                      <Form.Control
                        type="text" size="sm"
                        placeholder="Type your message here..."
                        value={this.state.inputMessage}
                        onChange={(event) => {
                          this.setState({ inputMessage: event.target.value })
                        }}
                      />
                    </Form>
                  </Col>
                  <Col xs="auto">
                    <Tooltip text={this.sendTooltipText()}>
                      <Button size="sm" variant="light" disabled={!this.canSendMessage()} onClick={(event) => {
                        this.sendMessage();
                      }}><VscSend/></Button>
                    </Tooltip>
                  </Col>
                </Row>
              </Card.Text>
            </Card.Body>
          </>
        }
      </div>
    );
  }
}

export default withPreferences(ChatNode, ["app.timezone"]);
